import type { ShopperProducts, ShopperSearch } from '@/scapi';
import type { SCAPIShopperProductsSchemas } from '@/types';
import { pickAnalyticsProductFields } from '@/lib/analytics/data-layer-mappers';
import { isMultipackBundledProductsShape } from '@/lib/multipack';

type Product = ShopperProducts.schemas['Product'];
type SearchHit = ShopperSearch.schemas['ProductSearchHit'];

export type ConvertProductToSearchHitOptions = {
    /** Wishlist line product id — resolves represented variant availability on master products. */
    lineItemProductId?: string;
};

type SoldByWeightResponseFields = Pick<
    SCAPIShopperProductsSchemas['Product'],
    | 'c_soldByWeight'
    | 'c_unit'
    | 'c_stepQuantity'
    | 'c_unitQuantity'
    | 'c_unitMeasure'
    | 'c_piecePrice'
    | 'c_pieceListPrice'
    | 'c_piecePriceMax'
    | 'c_pieceTieredPrices'
    | 'c_piecePromotionalPrice'
    | 'c_pieceAts'
>;

type OmnibusResponseFields = Pick<
    SCAPIShopperProductsSchemas['Product'],
    'c_priorBestPrice' | 'c_priceValidFrom' | 'c_priceValidTo'
>;

function copyOmnibusFields(source: unknown): OmnibusResponseFields {
    const product = source as OmnibusResponseFields;
    return {
        ...(product.c_priorBestPrice != null ? { c_priorBestPrice: product.c_priorBestPrice } : {}),
        ...(product.c_priceValidFrom ? { c_priceValidFrom: product.c_priceValidFrom } : {}),
        ...(product.c_priceValidTo ? { c_priceValidTo: product.c_priceValidTo } : {}),
    };
}

function copySoldByWeightFields(source: unknown): SoldByWeightResponseFields {
    const product = source as SoldByWeightResponseFields;
    return {
        c_soldByWeight: product.c_soldByWeight,
        c_unit: product.c_unit,
        c_stepQuantity: product.c_stepQuantity,
        c_unitQuantity: product.c_unitQuantity,
        c_unitMeasure: product.c_unitMeasure,
        c_piecePrice: product.c_piecePrice,
        c_pieceListPrice: product.c_pieceListPrice,
        c_piecePriceMax: product.c_piecePriceMax,
        c_pieceTieredPrices: product.c_pieceTieredPrices,
        c_piecePromotionalPrice: product.c_piecePromotionalPrice,
        c_pieceAts: product.c_pieceAts,
    };
}

function mapProductInventoryToSearchHit(
    inventory: Product['inventory']
): Pick<SearchHit, 'orderable' | 'inventory'> | undefined {
    if (!inventory) {
        return undefined;
    }

    return {
        orderable: inventory.orderable,
        inventory: {
            ats: inventory.ats,
            orderable: inventory.orderable,
            preorderable: inventory.preorderable,
            backorderable: inventory.backorderable,
        },
    };
}

/**
 * Converts a ShopperProducts.schemas['Product'] to ShopperSearch.schemas['ProductSearchHit'] format
 * This allows ProductTile and ProductGrid components to work with Product objects from wishlist
 *
 * @param product - The product to convert
 */
export function convertProductToProductSearchHit(
    product: Product,
    options?: ConvertProductToSearchHitOptions
): SearchHit {
    // Get the first image group's first image for the main image
    const firstImageGroup = product.imageGroups?.[0];
    const firstImage = firstImageGroup?.images?.[0];
    const productId = (product.id || product.productId || '') as string;
    const productName = (product.name || product.productName || '') as string;
    const productPrice = product.price ?? product.priceMax ?? 0;
    const c_url = product.c_url ?? (productId ? `/p/${productId}.html` : undefined);
    const bundledProducts = product.bundledProducts ?? [];
    const isMultipack = isMultipackBundledProductsShape(bundledProducts);
    // Assortment bundles may have multiple children — only multipack (1×N) uses the unit child for PIM weight.
    const unitProduct = isMultipack ? (bundledProducts[0]?.product ?? product) : product;
    const packQty = isMultipack ? bundledProducts[0]?.quantity : undefined;
    const availability = mapProductInventoryToSearchHit(product.inventory);
    const analyticsFields = pickAnalyticsProductFields(product);
    const productType = product.type
        ? {
              ...(product.type.bundle ? { bundle: true } : {}),
              ...(product.type.set ? { set: true } : {}),
              ...(product.type.item ? { item: true } : {}),
              ...(product.type.master ? { master: true } : {}),
              ...(product.type.variant ? { variant: true } : {}),
          }
        : undefined;
    const hitType = product.type?.bundle
        ? 'bundle'
        : product.type?.set
          ? 'set'
          : product.type?.variant
            ? 'variant'
            : product.type?.master
              ? 'master'
              : 'product';
    const soldByWeightFields = copySoldByWeightFields(product);
    const converted: SearchHit = {
        ...analyticsFields,
        productId,
        productName,
        hitType,
        ...(productType && Object.keys(productType).length > 0 ? { productType } : {}),
        ...(bundledProducts.length > 0 ? { bundledProducts } : {}),
        ...(typeof packQty === 'number' && packQty > 1 ? { c_multipackPackQty: packQty } : {}),
        ...(c_url ? { c_url } : {}),
        ...soldByWeightFields,
        ...copyOmnibusFields(product),
        price: productPrice,
        ...(product.priceMax != null ? { priceMax: product.priceMax } : {}),
        ...(product.tieredPrices?.length ? { tieredPrices: product.tieredPrices } : {}),
        currency: product.currency,
        image: firstImage
            ? {
                  disBaseLink: firstImage.disBaseLink || firstImage.link || '',
                  link: firstImage.disBaseLink || firstImage.link || '',
                  alt: firstImage.alt || product.name || '',
              }
            : undefined,
        imageGroups: product.imageGroups,
        variationAttributes: product.variationAttributes,
        variants: product.variants?.length
            ? product.variants.map((variant) => {
                  const variantWithPromos = variant as typeof variant & {
                      productPromotions?: Product['productPromotions'];
                  };
                  const variantInventory = (variant as { inventory?: Product['inventory'] }).inventory;
                  return {
                      productId: variant.productId,
                      variationValues: variant.variationValues,
                      orderable: variant.orderable,
                      ...(variant.price != null ? { price: variant.price } : {}),
                      ...copySoldByWeightFields(variant),
                      ...copyOmnibusFields(variant),
                      ...(variantWithPromos.productPromotions?.length
                          ? { productPromotions: variantWithPromos.productPromotions }
                          : {}),
                      ...(variant.tieredPrices?.length ? { tieredPrices: variant.tieredPrices } : {}),
                      inventory: variantInventory
                          ? {
                                ats: variantInventory.ats,
                                orderable: variantInventory.orderable,
                                preorderable: variantInventory.preorderable,
                                backorderable: variantInventory.backorderable,
                            }
                          : undefined,
                  };
              })
            : product.variationAttributes
              ? product.variationAttributes.map((attr) => ({
                    productId,
                    variationValues: attr.values?.reduce(
                        (acc, val) => {
                            if (attr.id && val.value) {
                                acc[attr.id] = val.value;
                            }
                            return acc;
                        },
                        {} as Record<string, string>
                    ),
                }))
              : undefined,
        inStock:
            product.inventory?.ats !== undefined ? product.inventory.ats > 0 : (product.inventory?.orderable ?? true),
        ...availability,
        // getPriceData reads productPromotions; search hits may also expose promotions
        ...(product.productPromotions?.length
            ? { productPromotions: product.productPromotions, promotions: product.productPromotions }
            : {}),
        representedProduct: {
            ...pickAnalyticsProductFields(
                typeof product.representedProduct === 'object' && product.representedProduct !== null
                    ? product.representedProduct
                    : undefined
            ),
            ...analyticsFields,
            // Bundle multipacks carry PIM weight on the unit child, not the bundle root.
            c_pimEatingStyles: unitProduct.c_pimEatingStyles ?? product.c_pimEatingStyles,
            c_pimProductHighlights: unitProduct.c_pimProductHighlights ?? product.c_pimProductHighlights,
            c_pimProductName: unitProduct.c_pimProductName ?? product.c_pimProductName,
            c_pimBrandName: unitProduct.c_pimBrandName ?? product.c_pimBrandName,
            c_pimEtiNetWeight: unitProduct.c_pimEtiNetWeight ?? product.c_pimEtiNetWeight,
            c_pimDrainedNetWeightDesc: unitProduct.c_pimDrainedNetWeightDesc ?? product.c_pimDrainedNetWeightDesc,
            c_pimDrainedNetWeight: unitProduct.c_pimDrainedNetWeight ?? product.c_pimDrainedNetWeight,
            c_pimDrainedNetWeightNotOil: unitProduct.c_pimDrainedNetWeightNotOil ?? product.c_pimDrainedNetWeightNotOil,
            c_productType:
                (unitProduct as SCAPIShopperProductsSchemas['Product']).c_productType ??
                (product as SCAPIShopperProductsSchemas['Product']).c_productType,
            c_eventStartDate: (product as SCAPIShopperProductsSchemas['Product']).c_eventStartDate,
            c_eventStoreId: (product as SCAPIShopperProductsSchemas['Product']).c_eventStoreId,
            c_pimUm: unitProduct.c_pimUm ?? product.c_pimUm,
            c_pimNetWeight: unitProduct.c_pimNetWeight ?? product.c_pimNetWeight,
            ...soldByWeightFields,
            id: unitProduct.id ?? productId,
        } as unknown as SearchHit['representedProduct'],
    };

    const lineItemProductId = options?.lineItemProductId;
    if (lineItemProductId && product.variants?.length) {
        const matchedVariant = product.variants.find((variant) => variant.productId === lineItemProductId);
        if (matchedVariant) {
            const matchedVariantInventory = (matchedVariant as { inventory?: Product['inventory'] }).inventory;
            const variantAvailability = mapProductInventoryToSearchHit(matchedVariantInventory);
            converted.representedProduct = {
                ...(converted.representedProduct as Record<string, unknown>),
                id: matchedVariant.productId,
                orderable: matchedVariant.orderable ?? variantAvailability?.orderable,
                inventory: variantAvailability?.inventory,
            } as SearchHit['representedProduct'];
        }
    }

    return converted;
}

/** Builds wishlist/analytics-ready search hits from a PDP product (and optional selected variant). */
export function toWishlistProductSearchHit(
    product: Product,
    variant?: ShopperProducts.schemas['Variant']
): { product: SearchHit; variant?: SearchHit } {
    const productHit = convertProductToProductSearchHit(product, {
        lineItemProductId: variant?.productId,
    });

    if (!variant?.productId) {
        return { product: productHit };
    }

    return {
        product: productHit,
        variant: {
            ...productHit,
            productId: variant.productId,
            ...(variant.price != null ? { price: variant.price } : {}),
            ...(variant.orderable != null ? { orderable: variant.orderable } : {}),
        },
    };
}
